<script setup>
/**
 * @fileoverview Root App component for Custom Mosaics.
 *
 * Users pick a pattern, then paint its tiles one by one with Original Ceramic
 * glazes. This component handles:
 * - Picking the starting design (shared link → last edited pattern → first pattern)
 * - Loading the Original Ceramic glaze palette from Algolia
 * - PostHog analytics integration and shared-link tracking
 * - Layout (header, controls sidebar, preview canvas)
 * - At `?print=1`, rendering only the specs sheet for the PDF service
 *
 * @component
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useMosaicStore } from './stores/mosaicStore';
import { usePostHog } from '../../shared/utils/usePostHog.js';
import { validateReturnUrl } from '../../shared/utils/validateReturnUrl.js';
import { trackViewSharedDesign } from './utils/trackingService';
import { DESIGN_PARAM_KEYS } from './utils/designCodec.js';
import { isPrintMode } from './utils/handOff.js';
import FireclayTileLogoHorizontal from './components/Shared/FireclayTileLogoHorizontal.vue';
import MosaicControls from './components/MosaicControls.vue';
import PatternPreview from './components/PatternPreview.vue';
import SpecSheet from './components/SpecSheet.vue';
import ConsultationLink from './components/ConsultationLink.vue';
import DownloadSpecsButton from './components/DownloadSpecsButton.vue';

const store = useMosaicStore();

/** The PDF service renders the spec sheet; skip analytics, Algolia and URL cleanup there. */
const printMode = isPrintMode(window.location.search);

// Pick the starting design before first render to avoid an empty flash. The
// spec sheet shows only the linked design, never a saved or default one.
const query = Object.fromEntries(new URLSearchParams(window.location.search));
if (printMode) store.applySharedParams(query);
else store.initialize(query);

/**
 * Removes shared-design params from the address bar once applied, so a refresh
 * restores the user's saved edits instead of re-applying the shared link.
 */
function stripDesignParams() {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of DESIGN_PARAM_KEYS) {
        if (url.searchParams.has(key)) {
            url.searchParams.delete(key);
            changed = true;
        }
    }
    if (changed) window.history.replaceState(window.history.state, '', url.toString());
}

// Track the return URL for the "Done" link
const returnUrl = ref('/');

/**
 * Component props for external configuration.
 * @property {string} appId - Algolia application ID
 * @property {string} apiKey - Algolia API key
 * @property {string} [posthogApiKey] - Optional PostHog API key for analytics
 * @property {string} [indexOriginal] - Algolia index name for Original Ceramic colors
 */
const props = defineProps({
    appId: { type: String, required: true },
    apiKey: { type: String, required: true },
    posthogApiKey: { type: String, required: false, default: '' },
    indexOriginal: { type: String, required: false, default: '' },
});

onMounted(() => {
    if (printMode) return;

    // Determine return URL for "Done" link
    // Priority: 1) returnTo query param, 2) document.referrer, 3) fallback to "/"
    const urlParams = new URLSearchParams(window.location.search);
    const returnToParam = urlParams.get('returnTo');

    if (returnToParam) {
        const validatedUrl = validateReturnUrl(returnToParam);
        if (validatedUrl) {
            returnUrl.value = validatedUrl;
        } else if (document.referrer && document.referrer !== window.location.href) {
            // Fallback to referrer if returnToParam validation fails
            try {
                const referrerUrl = new URL(document.referrer);
                // Only use referrer if it's from the same origin
                if (referrerUrl.origin === window.location.origin) {
                    returnUrl.value = referrerUrl.pathname + referrerUrl.search;
                }
            } catch {
                // Invalid referrer URL, fallback to "/"
            }
        }
    } else if (document.referrer && document.referrer !== window.location.href) {
        // Only use referrer if it's different from current page (not a refresh)
        try {
            const referrerUrl = new URL(document.referrer);
            // Only use referrer if it's from the same origin
            if (referrerUrl.origin === window.location.origin) {
                returnUrl.value = referrerUrl.pathname + referrerUrl.search;
            }
        } catch {
            // Invalid referrer URL, fallback to "/"
        }
    }

    // Load Original Ceramic colors from Algolia
    store.initAlgolia(props.appId, props.apiKey, props.indexOriginal);

    // Initialize PostHog if API key is provided
    if (props.posthogApiKey) {
        usePostHog(props.posthogApiKey);
    }

    // Track if user arrived from a shared link, then clean the URL
    trackViewSharedDesign();
    stripDesignParams();
});

onBeforeUnmount(() => {
    // Clean up store resources to prevent memory leaks
    store.cleanup();
});
</script>

<template>
    <SpecSheet v-if="printMode" />
    <div v-else class="h-screen flex flex-col">
        <header
            class="h-auto lg:h-[79px] px-4 lg:px-[50px] py-2 lg:py-4 bg-white border-b border-dryline flex flex-wrap lg:flex-nowrap items-center gap-x-4 lg:gap-x-10 gap-y-2 flex-shrink-0"
        >
            <FireclayTileLogoHorizontal
                alt="Fireclay Tile"
                class="w-[120px] lg:w-[178px] h-auto lg:pr-1.5 block shrink-0"
            />
            <h1 class="sr-only">Fireclay Tile Custom Mosaics</h1>
            <!-- Full-width second row on small screens; inline after the logo on desktop -->
            <div class="order-last w-full lg:order-none lg:w-auto lg:flex-1">
                <ConsultationLink />
            </div>
            <div class="ml-auto items-center gap-2 md:gap-3 flex">
                <DownloadSpecsButton />
                <a
                    :href="returnUrl"
                    class="px-2 py-1.5 md:px-4 md:py-3 rounded-[50px] border border-dryline justify-center items-center gap-2 flex"
                >
                    <div
                        class="text-dryline text-[10px] md:text-xs font-normal font-body uppercase leading-tight tracking-wide"
                    >
                        Done
                    </div>
                </a>
            </div>
        </header>
        <div class="lg:hidden w-full bg-engobe-100 pt-1 text-center text-dryline">
            <span class="text-xs">
                Please note, this tool is best viewed on larger screen sizes.
            </span>
        </div>

        <!-- Main content area: full-height grid with sidebar + canvas -->
        <div
            class="flex-1 min-h-0 grid lg:grid-cols-[400px,1fr] xl:grid-cols-[500px,1fr] gap-6 items-stretch p-2 lg:p-4 bg-engobe-100"
        >
            <!-- Left: Controls -->
            <MosaicControls class="order-2 lg:order-1" />

            <!-- Right: Pattern preview / paint canvas -->
            <PatternPreview class="order-1 lg:order-2" />
        </div>
    </div>
</template>
